import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  BadgeCheck,
  Award,
  Lock,
  Stamp,
  ArrowUpRight,
  HeartHandshake,
} from "lucide-react";
import { PageHero, GoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { IMG } from "@/constants/images";

const CREDENTIALS = [
  { icon: Stamp, label: "Florida State Commissioned Notary Public" },
  { icon: Award, label: "NNA Member & Certified Loan Signing Agent" },
  { icon: ShieldCheck, label: "Florida Commissioned for Remote Online Notarization (RON)" },
  { icon: Lock, label: "DoD Secret Cleared" },
  { icon: BadgeCheck, label: "Bonded & Insured (E&O)" },
  { icon: Lock, label: "Background Screened — Annually Renewed" },
  { icon: Award, label: "Certified Senior Advisor (CSA) Candidate" },
  { icon: HeartHandshake, label: "End-of-Life (EoL) Doula Candidate" },
  { icon: Stamp, label: "45 Years · DoD / DLA / U.S. State Department Experience" },
  { icon: Award, label: "DBM & MBA · Doctor of Business Management" },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About OGO Notary"
        title="Precision Built on a Foundation of Global Trust."
        lead="Forty-five years of mission-critical document and operational discipline — translated into a flawless, executive-grade notary practice on Florida's Space Coast."
        backdrop={IMG.signingTable}
        backdropOpacity={0.55}
        bright
      />

      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Headshot */}
            <div className="lg:col-span-5">
              <div className="relative border border-white/10 bg-[#1A2B45] aspect-[4/5] overflow-hidden group">
                <img
                  src={IMG.headshotGreg}
                  alt="Dr. Gregory Byers — Founder, OGO Notary & Signing Services"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
                  data-testid="about-founder-headshot"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/85 to-transparent pt-20 pb-6 px-6">
                  <div className="eyebrow mb-2">Founder</div>
                  <div className="font-serif-display text-2xl sm:text-3xl text-white leading-tight">
                    Dr. Gregory Byers
                  </div>
                  <p className="mt-2 text-slate-300 text-xs leading-relaxed">
                    Corporate operations executive — Department of Defense,
                    DLA, U.S. State Department.
                  </p>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="lg:col-span-7">
              <SectionEyebrow className="mb-4">About Our Founder</SectionEyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
                A Legacy of Flawless Operational Execution.
              </h2>
              <div className="mt-8 space-y-6 text-slate-300 leading-relaxed text-base">
                <p>
                  Founder and Chief Operator,{" "}
                  <span className="text-white">Dr. Gregory A. Byers</span>,
                  brings unparalleled discipline, precision, and security to
                  the notary process, honed over{" "}
                  <span className="text-white">45 years</span> working directly
                  with the{" "}
                  <span className="text-white">Department of Defense (DoD)</span>,
                  the{" "}
                  <span className="text-white">Defense Logistics Agency (DLA)</span>,
                  and the{" "}
                  <span className="text-white">U.S. State Department</span>.
                </p>
                <p>
                  Dr. Byers has personally overseen and executed{" "}
                  <span className="text-white">
                    thousands of high-stakes international document packages,
                    proposals, employment contracts, joint venture agreements,
                    and Secretary of State apostilles
                  </span>{" "}
                  across dozens of countries.
                </p>
                <p>
                  His specialized expertise extends beyond traditional
                  notarization. He has formed numerous foreign legal entities
                  to serve the US DoD, navigating the complex intersection of
                  federal requirements and international statutes.
                </p>
                <p>
                  <span className="text-white">OGO Notary &amp; Signing Services</span>{" "}
                  was built on this foundation of elite operational rigor. We
                  don&apos;t just facilitate signatures; we provide{" "}
                  <em className="text-[#D4AF37] not-italic">Simple. Safe. Secure.</em>{" "}
                  operational engineering.
                </p>
                <p>
                  Every engagement, from a local mobile notary appointment in
                  Brevard County to a complex corporate RON session, is
                  executed with meticulous compliance and unwavering discipline.
                </p>
              </div>

              <div className="mt-10 border-l-2 border-[#D4AF37] pl-6">
                <div className="eyebrow mb-2">The OGO Standard</div>
                <p className="font-serif-display text-2xl text-white leading-snug">
                  &quot;When precision matters, trust the OGO ecosystem for flawless
                  execution.&quot;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="relative overflow-hidden py-24 sm:py-32">
        <FadedBackdrop src={IMG.realEstateSigning} opacity={0.22} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Credentials</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Verified. Bonded. Background-screened.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              Our credentials aren&apos;t decorative — they&apos;re how title companies,
              attorneys, and individual signers know the work will hold up
              under scrutiny.
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CREDENTIALS.map((c, i) => (
              <li
                key={i}
                data-testid={`about-credential-${i}`}
                className="flex items-center gap-4 border border-white/10 bg-[#1A2B45] px-6 py-5 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
                  <c.icon className="w-5 h-5" />
                </div>
                <span className="text-white text-sm sm:text-base">
                  {c.label}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-14 flex flex-wrap gap-4">
            <GoldButton
              as={Link}
              to="/contact"
              data-testid="about-book-btn"
            >
              Schedule a Signing <ArrowUpRight className="w-4 h-4" />
            </GoldButton>
          </div>
        </div>
      </section>
    </>
  );
}
